import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { IconBookmarkSmall } from '@medifind/icons';
import { normalizeNumbers } from '@medifind/router';
import { deepEquals, eventDispatch, useMediaQueryDown } from '@medifind/utils';
import { addBookmark, logEventGA4, removeBookmark, useAuthentication, useBookmarks, useShareBookmark } from '@medifind/zustand';
import { Button } from './inputs';

const checkForBookmark = (bookmarks, bookmarkData) => {
  if (bookmarkData?.type) {
    const { type, id, secondaryLookupId, data } = normalizeNumbers(bookmarkData);
    if (id) {
      return (bookmarks[type] || []).find((o) => o.id === bookmarkData.id);
    } else {
      const i = (bookmarks[type] ?? []).findIndex((o) => {
        if (secondaryLookupId && secondaryLookupId !== o.secondaryLookupId) {
          return false;
        }
        return deepEquals(data, o.data) && type === o.type;
      });
      return i === -1 ? null : bookmarks[type][i];
    }
  }
};
const Bookmark = (props) => {
  const { callback, isListItem, onClick, color, data: dataOverride, textOverride } = props;
  const bookmarkStoreData = useShareBookmark((state) => state.bookmark);
  const { type, bookmarkId, bookmarkName, data, secondaryLookupId, secondaryLookupData } = dataOverride || bookmarkStoreData;

  const bookmarks = useBookmarks((state) => state.bookmarks);
  const { profile } = useAuthentication();
  const errorTitleLookup = {
    condition: `Save ${bookmarkName} To My Profile`,
    procedure: `Save ${bookmarkName} To My Profile`,
    doctor: 'Save This Doctor',
    recent: 'Save This Article',
    publication: 'Save This Article',
    symptom: 'Save This Article',
    trial: 'Save This Clinical Trial',
    drug: 'Save This Drug',
    specialty: 'Save This Specialty',
    'symptom-checker': 'Save Your Results',
    'trial-search': 'Save Your Results',
    'second-opinion': 'Save Your Results',
    'find-specialist': 'Save Your Results',
    'publication-search': 'Save Your Results',
    'clinical-trial-search': 'Save Your Results',
    'latest-advance-search': 'Save Your Results',
    'drug-search': 'Save Your Results',
    'procedure-search': 'Save Your Results',
    'find-doctor': 'Save Your Results',
    organization: 'Save This Organization',
    default: 'Save Your Result',
  };
  const typeLookup = {
    specialty: 'save this specialty',
    // 'article',
    doctor: `save ${bookmarkName}`,
    organization: `save ${bookmarkName}`,
    recent: 'save this recent advance',
    publication: 'save this publication',
    trial: 'save this clinical trial',
    condition: `add ${bookmarkName}`,
    procedure: `add ${bookmarkName}`,
    symptom: `add ${bookmarkName}`,
    drug: 'save this drug',
    'symptom-checker': 'save these symptom checker results',
    'trial-search': 'save these clinical trial search results',
    'second-opinion': 'save these second opinion results',
    'find-specialist': 'save these specialty results',
    'publication-search': 'save these search results',
    'clinical-trial-search': 'save these clinical trial search results',
    'latest-advance-search': 'save these latest advance search results',
    'drug-search': 'save these drug search results',
    'procedure-search': 'save these procedure search results',
    'find-doctor': 'save these doctor search results',
    default: 'save this information',
  };

  const then = (bookmark) => {
    if (!bookmark) {
      logEventGA4('bookmark', { action: 'save', type });
    } else {
      logEventGA4('bookmark', { action: 'unsave', type });
    }
    callback && callback(null, { hasBookmark: !!bookmark });
  };
  const bookmarkData = useMemo(() => {
    const res = { id: bookmarkId, name: bookmarkName, data: data, type, secondaryLookupId, secondaryLookupData };
    return res;
  }, [bookmarkId, bookmarkName, data, type, secondaryLookupId, secondaryLookupData]);

  const handleClick = (bookmark) => {
    if (type) {
      if (profile) {
        if (!bookmark) addBookmark(bookmarkData);
        else removeBookmark(bookmark);
        then(bookmark);
      } else {
        eventDispatch('activateAuth', {
          errorTitle: errorTitleLookup[type] || errorTitleLookup.default,
          errorMessage: `Sign in or create a free MediFind account to ${
            typeLookup[type] || typeLookup.default
          } to your personalized health dashboard${
            type === 'condition' ? ' and get the care you deserve' : type === 'doctor' ? ' and find the care you deserve' : ''
          }.`,
          authType: 'login',
          icon: 'save',
          redirectAction: 'bookmark',
          redirectData: bookmarkData,
        });
      }

      if (onClick) onClick();
    }
  };
  const getText = ({ bookmark, textOverride }) => {
    if (textOverride) {
      const { saveText = '', unsaveText = '' } = textOverride;
      return bookmark ? unsaveText : saveText;
    }
    return bookmark ? 'Unsave' : 'Save';
  };

  const bookmark = useMemo(() => (bookmarks ? checkForBookmark(bookmarks, bookmarkData) : null), [bookmarks, bookmarkData]);

  const button = (
    <BookmarkButton
      {...props}
      onClick={(event) => {
        handleClick(bookmark);
      }}
      bookmark={bookmark}
      text={getText({ bookmark, textOverride })}
      color={color}
    />
  );
  return <>{isListItem ? <li>{button}</li> : button}</>;
};
Bookmark.propTypes = {
  onClick: PropTypes.func,
  callback: PropTypes.func,
  icons: PropTypes.object,
  buttonType: PropTypes.string,
  disabled: PropTypes.bool,
  iconOnly: PropTypes.bool,
  fill: PropTypes.bool,
  isListItem: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  iconOnlyAt: PropTypes.string,
  color: PropTypes.string, // TODO make this .any;
};

const BookmarkButton = ({
  disabled,
  iconOnly,
  className,
  fill,
  icons,
  onClick,
  text,
  bookmark,
  isListItem,
  type,
  buttonType,
  iconOnlyAt,
  style,
  color,
  classes,
}) => {
  const isMobileXS = useMediaQueryDown(iconOnlyAt ?? 'xs');

  let buttonColor = color;
  let iconColor = {};
  if (iconOnly) {
    if (!bookmark) iconColor = { color: 'lightGreyText' };
  } else if (color === 'darkBlue') {
    iconColor = { color: 'darkBlue', fill: !!bookmark };
  } else if (bookmark && !isListItem) {
    iconColor = { color: 'white' };
  }
  if (color === 'transparent') {
    iconColor = { color: 'blue', fill: !!bookmark };
  } else if (color === 'transparent-blue') {
    iconColor = { color: 'blue', fill: !!bookmark };
  } else if (color === 'blue') {
    iconColor = { color: 'blue', fill: !!bookmark };
    if (bookmark) buttonColor = 'blue';
    if (!bookmark) buttonColor = 'light-blue';
  } else if (color === 'light-blue') {
    iconColor = { color: 'buttonSecondaryBlue', fill: !!bookmark };
  } else if (color === 'white-no-border') {
    iconColor = { color: 'blue', fill: !!bookmark };
  }
  if (iconOnly) buttonColor = color || 'transparent';
  if (isListItem) color = color || 'none';

  if (icons == null) {
    icons = {
      bookmarked: (
        <IconBookmarkSmall
          fill={fill}
          {...iconColor}
          width={isMobileXS && iconOnlyAt ? 16 : 12}
          height={isMobileXS && iconOnlyAt ? 21 : 15}
        />
      ),
      notBookmarked: (
        <IconBookmarkSmall
          fill={fill}
          {...iconColor}
          width={isMobileXS && iconOnlyAt ? 16 : 12}
          height={isMobileXS && iconOnlyAt ? 21 : 15}
        />
      ),
    };
  }
  return (
    <Button
      onClick={(event) => {
        onClick(event);
        event.preventDefault();
        return false;
      }}
      type={buttonType}
      className={className}
      style={style}
      iconOnlyAt={iconOnlyAt}
      noPadding={!!isListItem}
      color={buttonColor}
      {...(disabled ? { disabled } : {})}
      id={`bookmark-${type}-${bookmark ? 'remove' : 'add'}`}
      focus={iconOnly ? 'outline' : 'underline'}
      {...(iconOnly ? { screenReaderText: bookmark ? 'Remove Bookmark' : 'Bookmark' } : {})}
      label={!iconOnly ? text : null}
      iconBefore={bookmark ? icons.bookmarked : icons.notBookmarked}
      classes={classes}
    />
  );
};

BookmarkButton.propTypes = {
  icons: PropTypes.object,
  disabled: PropTypes.bool,
  iconOnly: PropTypes.bool,
  bookmark: PropTypes.object,
  classes: PropTypes.object,
  data: PropTypes.any,
  text: PropTypes.string,
  fill: PropTypes.bool,
  onClick: PropTypes.func,
  isListItem: PropTypes.bool,
  className: PropTypes.string,
  type: PropTypes.string,
  buttonType: PropTypes.string,
  style: PropTypes.object,
  iconOnlyAt: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'xxl']),
  color: PropTypes.string,
};

export { Bookmark as default, Bookmark };
